import React from 'react';
import Icon from '../../../components/AppIcon';
import { formatCurrencyINR } from '../../../utils/currency';

const PortfolioSummary = ({ assets }) => {
  const totalValue = assets.reduce((sum, asset) => sum + (Number(asset.currentValue) || 0), 0);
  const totalCost = assets.reduce((sum, asset) => sum + (asset.purchaseValue || asset.currentValue), 0);
  const totalGain = totalValue - totalCost;
  const gainPercentage = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;
  
  const liquidAssets = assets.filter(asset => asset.liquidity === 'Liquid');
  const liquidValue = liquidAssets.reduce((sum, asset) => sum + asset.currentValue, 0);
  const liquidPercentage = totalValue > 0 ? (liquidValue / totalValue) * 100 : 0;

  const projectedValue = assets.reduce((sum, asset) => {
    const years = 1;
    const futureValue = asset.currentValue * Math.pow(1 + (asset.expectedReturn / 100), years);
    return sum + futureValue;
  }, 0);
  
  const projectedGain = projectedValue - totalValue;
  const projectedGainPercentage = totalValue > 0 ? (projectedGain / totalValue) * 100 : 0;

  const summaryCards = [
    {
      title: 'Total Portfolio Value',
      value: formatCurrencyINR(totalValue, { compact: true }),
      change: `${gainPercentage >= 0 ? '+' : ''}${gainPercentage.toFixed(1)}%`,
      changeType: gainPercentage >= 0 ? 'positive' : 'negative',
      icon: 'Wallet',
      description: `Total gain: ${formatCurrencyINR(totalGain, { compact: true })}`
    },
    {
      title: 'Liquid Assets',
      value: formatCurrencyINR(liquidValue, { compact: true }),
      change: `${liquidPercentage.toFixed(1)}% of portfolio`,
      changeType: 'neutral',
      icon: 'Droplets',
      description: `${liquidAssets.length} liquid assets`
    },
    {
      title: 'Projected 1Y Value',
      value: formatCurrencyINR(projectedValue, { compact: true }),
      change: `+${projectedGainPercentage.toFixed(1)}%`,
      changeType: 'positive',
      icon: 'TrendingUp',
      description: `Expected gain: ${formatCurrencyINR(projectedGain, { compact: true })}`
    },
    {
      title: 'Asset Count',
      value: assets.length.toString(),
      change: `${new Set(assets.map(a => a.type)).size} categories`,
      changeType: 'neutral',
      icon: 'Package',
      description: 'Diversified portfolio'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {summaryCards.map((card, index) => (
        <div key={index} className="bg-card rounded-lg border border-border p-6 hover:shadow-elevated transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={card.icon} size={20} className="text-primary" />
            </div>
            <span className={`
              text-sm font-medium px-2 py-1 rounded-full
              ${card.changeType === 'positive' ? 'text-accent bg-accent/10' :
                card.changeType === 'negative'? 'text-destructive bg-destructive/10' : 'text-muted-foreground bg-muted/50'
              }
            `}>
              {card.change}
            </span>
          </div>
          
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">{card.title}</h3>
            <p className="text-2xl font-bold text-foreground">{card.value}</p>
            <p className="text-xs text-muted-foreground">{card.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PortfolioSummary;